// effects/personal-details.effects.ts
import { inject } from '@angular/core'
import { createEffect } from '@ngrx/effects'
import { Actions, ofType } from '@ngrx/effects'
import { RoleService } from '../../services/role.service'
import { ToastrService } from 'ngx-toastr'
import { catchError, exhaustMap, map, of, switchMap } from 'rxjs'
import {
  createAssignedTo,
  createAssignedToFailure,
  createAssignedToSuccess,
  createRole,
  createRoleFailure,
  createRoleSuccess,
  deleteAssignedTo,
  deleteAssignedToFailure,
  deleteAssignedToSuccess,
  deleteRole,
  deleteRoleFailure,
  deleteRoleSuccess,
  loadAssignedTo,
  loadAssignedToFailure,
  loadAssignedToSuccess,
  loadRoleById,
  loadRoleByIdFailure,
  loadRoleByIdSuccess,
  loadRoles,
  loadUnassignedUsers,
  loadUnassignedUsersFailure,
  loadUnassignedUsersSuccess,
  updateRole,
  updateRoleFailure,
  updateRoleSuccess,
  upsertValidUpto,
  upsertValidUptoSuccess
} from './role.actions'
import { loadRolesFailure, loadRolesSuccess } from './role.actions'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'

export const loadRoles$ = createEffect(
  (actions$ = inject(Actions), roleService = inject(RoleService)) =>
    actions$.pipe(
      ofType(loadRoles),
      switchMap(action =>
        roleService.getRoles(action.params, action.page, action.limit).pipe(
          map(res => {
            return loadRolesSuccess({
              roles: res.roles,
              totalCount: res.totalCount
            })
          }),
          catchError(error => {
            console.error('Load roles failed:', error.message) // Log errors here
            return of(loadRolesFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

export const createRole$ = createEffect(
  (
    actions$ = inject(Actions),
    roleService = inject(RoleService),
    toastr = inject(ToastrService), // Inject the ToastrService
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(createRole),
      exhaustMap(({ name, permissions, isStandard, businessIds }) =>
        roleService.createRole({ name, permissions, isStandard, businessIds }).pipe(
          map(created => {
            // Dispatch the success action
            const successAction = createRoleSuccess({ role: created })

            // Show a success message using Toastr
            toastr.success('Role created successfully!', 'Success')
            // store.dispatch(loadRoles({ params: null, page: 1, limit: 10 }))
            modalService.dismissAll()
            return successAction
          }),
          catchError(error => {
            // Optionally, show an error message
            toastr.error(error.error.message, 'Error')
            return of(createRoleFailure({ error }))
          })
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const updateRole$ = createEffect(
  (
    actions$ = inject(Actions),
    roleService = inject(RoleService),
    toastr = inject(ToastrService),
    store = inject(Store),
    modalService = inject(NgbModal)
  ) =>
    actions$.pipe(
      ofType(updateRole),
      exhaustMap(({ id, name, permissions, isStandard, businessId }) =>
        roleService
          .updateRole({
            id,
            name,
            permissions,
            isStandard,
            businessId
          })
          .pipe(
            map(updatedRole => {
              toastr.success('Role updated successfully!', 'Success')

              // Dispatch loadBusinessById after the update is successful
              // store.dispatch(loadRoles({ params: null }))
              modalService.dismissAll()
              // Dispatch success action to update the store
              return updateRoleSuccess({ role: updatedRole })
            }),
            catchError(error => {
              toastr.error('Failed to update Role', 'Error') // Show error message
              return of(updateRoleFailure({ error }))
            })
          )
      )
    ),
  { functional: true }
)

export const loadRoleById$ = createEffect(
  (actions$ = inject(Actions), roleService = inject(RoleService)) =>
    actions$.pipe(
      ofType(loadRoleById),
      switchMap(({ id }) =>
        roleService.getRoleById(id).pipe(
          map(role => {
            // console.log('role', role)
            return loadRoleByIdSuccess({ role })
          }),
          catchError(error => of(loadRoleByIdFailure({ error: error.message })))
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const loadAssignedTo$ = createEffect(
  (actions$ = inject(Actions), roleService = inject(RoleService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(loadAssignedTo),
      switchMap(action =>
        roleService.getAssignedTo(action.roleId).pipe(
          map(res => loadAssignedToSuccess({ assignedTo: res })),
          catchError(error => {
            toastr.error('Failed to fetch users: ' + error.message)
            return of(loadAssignedToFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

export const createAssignedTo$ = createEffect(
  (actions$ = inject(Actions), roleService = inject(RoleService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(createAssignedTo),
      exhaustMap(({ roleId, userData, businessId }) =>
        roleService.createAssignedTo(roleId, userData, businessId).pipe(
          map(d => {
            toastr.success('assigned successfully!', 'Success')
            return createAssignedToSuccess({ assignedTo: d })
          }),
          catchError(error => {
            toastr.error('Failed to Assign: ' + error.message)
            return of(createAssignedToFailure({ error: error.message }))
          })
        )
      )
    ),
  { functional: true }
)

export const upsertValidUpto$ = createEffect(
  (actions$ = inject(Actions), roleService = inject(RoleService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(upsertValidUpto),
      exhaustMap(({ id, validUpto }) =>
        roleService.upsertValidUpto(id, validUpto).pipe(
          map(d => {
            const successAction = upsertValidUptoSuccess({ assignedTo: d })
            toastr.success('Updated successfully!', 'Success')
            return successAction
          }),
          catchError(error => {
            toastr.error(error.error.message, 'Error')
            return of(createRoleFailure({ error }))
          })
        )
      )
    ),
  { functional: true, dispatch: true }
)

export const deleteAssignedTo$ = createEffect(
  (actions$ = inject(Actions), roleService = inject(RoleService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(deleteAssignedTo),
      exhaustMap(action =>
        roleService.deleteAssignedTo(action.id).pipe(
          map(d => deleteAssignedToSuccess({ assignedTo: d })),
          catchError(error => {
            toastr.error('Failed to delete assignment: ' + error.message)
            return of(deleteAssignedToFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const deleteRole$ = createEffect(
  (actions$ = inject(Actions), roleService = inject(RoleService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(deleteRole),
      exhaustMap(action =>
        roleService.deleteRole(action.id).pipe(
          map(() => {
            toastr.success('Role deleted successfully!')
            return deleteRoleSuccess({ id: action.id })
          }),
          catchError(error => {
            toastr.error('Failed to delete Role: ' + error.message)
            return of(deleteRoleFailure({ error }))
          })
        )
      )
    ),
  { functional: true }
)

export const loadUnassignedUsers$ = createEffect(
  (actions$ = inject(Actions), roleService = inject(RoleService), toastr = inject(ToastrService)) =>
    actions$.pipe(
      ofType(loadUnassignedUsers),
      switchMap(action =>
        roleService
          .getUnssignedUsers(action.roleId, action.businessId, action.businessIdtoAssign)
          .pipe(
            map(res =>
              loadUnassignedUsersSuccess({
                users: res.users,
                totalUserCount: res.totalCount
              })
            ),
            catchError(error => {
              toastr.error('Failed to fetch users: ' + error.message)
              return of(loadUnassignedUsersFailure({ error: error.message }))
            })
          )
      )
    ),
  { functional: true }
)

export const roleEffects = {
  loadRoles$,
  createRole$,
  updateRole$,
  loadRoleById$,
  createAssignedTo$,
  upsertValidUpto$,
  deleteAssignedTo$,
  loadAssignedTo$,
  deleteRole$,
  loadUnassignedUsers$
}
